import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2, Plus, Search, Loader2, MoreHorizontal, Pencil, Copy, Trash, FileSpreadsheet } from "lucide-react";
import { propertyApi, associationApi } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import { Property, PropertySearchParams, Association } from "@/models";
import { DeletePropertyDialog } from "@/components/properties/DeletePropertyDialog";
import { PropertyBulkUploadModal } from "@/components/properties/PropertyBulkUploadModal";
import { extractDataArray } from "@/utils/apiUtils";
import { PaginationControls } from "@/components/shared/PaginationControls";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";

const ITEMS_PER_PAGE = 10;

const Properties = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [associationId, setAssociationId] = useState<string>("all");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<Property | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const searchParams: PropertySearchParams = {
    search: searchTerm,
    association_id: associationId !== "all" ? parseInt(associationId) : undefined,
    limit: ITEMS_PER_PAGE,
    offset: (currentPage - 1) * ITEMS_PER_PAGE
  };

  const { data: propertiesResponse, isLoading: isLoadingProperties } = useQuery({
    queryKey: ['properties', searchParams],
    queryFn: propertyApi.list,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading properties",
          description: error.message || "Failed to fetch properties",
        });
      }
    }
  });

  const { data: associationsResponse = [], isLoading: isLoadingAssociations } = useQuery({
    queryKey: ['associations', {} as any],
    queryFn: associationApi.list,
    meta: {
      onError: (error: any) => {
        toast({
          variant: "destructive",
          title: "Error loading associations",
          description: error.message || "Failed to fetch associations",
        });
      }
    }
  });

  const properties = propertiesResponse?.data || [];
  const totalProperties = propertiesResponse?.metadata?.total_count || 0;
  const totalPages = Math.max(1, Math.ceil(totalProperties / ITEMS_PER_PAGE));
  const associations = extractDataArray(associationsResponse);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const deleteMutation = useMutation({
    mutationFn: (id: number) => propertyApi.delete(id),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Property deleted successfully",
      });
      
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      
      setIsDeleteDialogOpen(false);
      setPropertyToDelete(null);
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to delete property",
      });
    }
  });

  const handleDelete = () => {
    if (!propertyToDelete) return;
    deleteMutation.mutate(propertyToDelete.id);
  };

  const handleDuplicate = (property: Property) => {
    toast({
      title: "Info",
      description: "Property duplication feature coming soon",
    });
  };

  const handleRowClick = (property: Property, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('[data-actions-menu]')) {
      return;
    }
    navigate(`/backoffice/properties/${property.id}/edit`);
  };

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Properties</h1>
            <p className="text-muted-foreground">
              Manage properties in associations
            </p>
          </div>
          <div className="flex space-x-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" /> Add Property
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/backoffice/properties/add">
                    <Plus className="mr-2 h-4 w-4" /> Add Single Property
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsBulkUploadModalOpen(true)}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" /> Add Multiple Properties
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Filter Properties</CardTitle>
            <CardDescription>
              Search and filter the list of properties
            </CardDescription>
            <div className="flex flex-col gap-4 mt-4 md:flex-row">
              <div className="relative w-full md:w-2/3">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search by address, city, state or zip code..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>
              <div className="w-full md:w-1/3">
                <Select
                  value={associationId}
                  onValueChange={(value) => {
                    setAssociationId(value);
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by association" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Associations</SelectItem>
                    {associations.map((association: Association) => (
                      <SelectItem key={association.id} value={association.id?.toString() || "0"}>
                        {association.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingProperties || isLoadingAssociations ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
              </div>
            ) : properties.length > 0 ? (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Suite/Unit</TableHead>
                        <TableHead>Association</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>City</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>ZIP Code</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {properties.map((property: Property) => {
                        const association = associations.find(
                          (a: Association) => a.id === property.association_id
                        );
                        
                        return (
                          <TableRow 
                            key={property.id} 
                            className="cursor-pointer"
                            onClick={(e) => handleRowClick(property, e)}
                          >
                            <TableCell>{property.suite_unit || "—"}</TableCell>
                            <TableCell>{association?.name || `Association ID: ${property.association_id}`}</TableCell>
                            <TableCell>{property.address}</TableCell>
                            <TableCell>{property.city || "—"}</TableCell>
                            <TableCell>{property.state || "—"}</TableCell>
                            <TableCell>{property.zip_code || "—"}</TableCell>
                            <TableCell className="text-right" data-actions-menu>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                                    <span className="sr-only">Open menu</span>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                  <DropdownMenuItem asChild>
                                    <Link to={`/backoffice/properties/${property.id}/edit`}>
                                      <Pencil className="mr-2 h-4 w-4" />
                                      Edit
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleDuplicate(property)}>
                                    <Copy className="mr-2 h-4 w-4" />
                                    Duplicate
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-red-600"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPropertyToDelete(property);
                                      setIsDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash className="mr-2 h-4 w-4" />
                                    Remove
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                
                {totalPages > 1 && (
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <Building2 className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium">No properties found</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {searchTerm || associationId !== "all"
                    ? "Try a different search term or filter"
                    : "No properties have been added yet"}
                </p>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button>
                      <Plus className="mr-2 h-4 w-4" /> Add Property
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center">
                    <DropdownMenuItem asChild>
                      <Link to="/backoffice/properties/add">
                        <Plus className="mr-2 h-4 w-4" /> Add Single Property
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setIsBulkUploadModalOpen(true)}>
                      <FileSpreadsheet className="mr-2 h-4 w-4" /> Add Multiple Properties
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <DeletePropertyDialog 
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        isPending={deleteMutation.isPending}
      />
      
      <PropertyBulkUploadModal
        open={isBulkUploadModalOpen}
        onOpenChange={setIsBulkUploadModalOpen}
      />
    </BackofficeLayout>
  );
};

export default Properties;
